import { useState, useEffect, useCallback } from "react";
import { API_BASE as API } from '../config';

// Config field display names + current bot defaults (conservative RENDER profile)
const CONFIG_FIELDS = [
  { key: "tp",           label: "Take Profit %",      fmt: v => `${v?.toFixed(2)}%` },
  { key: "step",         label: "SO Step %",           fmt: v => `${v?.toFixed(2)}%` },
  { key: "vol_scale",    label: "Volume Scale",        fmt: v => `${v?.toFixed(2)}x` },
  { key: "step_scale",   label: "Step Scale",          fmt: v => `${v?.toFixed(2)}x` },
  { key: "max_so",       label: "Max Safety Orders",   fmt: v => `${Math.round(v)}` },
  { key: "bo_pct",       label: "Base Order %",        fmt: v => `${v?.toFixed(1)}%` },
  { key: "so_pct",       label: "Safety Order %",      fmt: v => `${v?.toFixed(1)}%` },
  { key: "rsi_threshold",label: "RSI Threshold",       fmt: v => `${Math.round(v)}` },
  { key: "trailing",     label: "Trailing TP",         fmt: v => v ? "ON" : "OFF" },
  { key: "compound_cap", label: "Compound Cap",        fmt: v => `${v?.toFixed(1)}x` },
];

const Badge = ({ children, color = "zinc" }) => {
  const colors = {
    green: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
    red: "bg-red-500/10 text-red-400 border-red-500/20",
    yellow: "bg-amber-500/10 text-amber-400 border-amber-500/20",
    blue: "bg-blue-500/10 text-blue-400 border-blue-500/20",
    teal: "bg-teal-500/10 text-teal-400 border-teal-500/20",
    purple: "bg-purple-500/10 text-purple-400 border-purple-500/20",
    zinc: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20",
  };
  return (
    <span className={`px-2 py-0.5 text-[10px] font-mono border rounded-full ${colors[color] || colors.zinc}`}>
      {children}
    </span>
  );
};

export default function AutopilotTab({ token }) {
  const [status, setStatus] = useState(null);
  const [gems, setGems] = useState([]);
  const [learning, setLearning] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [deployModal, setDeployModal] = useState(null); // { gem, botStatus }
  const [deployLoading, setDeployLoading] = useState(false);
  const [currentBotConfig, setCurrentBotConfig] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    if (!token) return;
    try {
      const [statusRes, gemsRes, learnRes, botRes] = await Promise.allSettled([
        fetch(`${API}/api/autopilot/status`, { headers }),
        fetch(`${API}/api/autopilot/gems`, { headers }),
        fetch(`${API}/api/autopilot/learning`, { headers }),
        fetch(`${API}/api/eu-bot/dca/all-bots`, { headers }),
      ]);

      if (statusRes.status === "fulfilled" && statusRes.value.ok) {
        setStatus(await statusRes.value.json());
      }
      if (gemsRes.status === "fulfilled" && gemsRes.value.ok) {
        const g = await gemsRes.value.json();
        setGems(g.gems || []);
      }
      if (learnRes.status === "fulfilled" && learnRes.value.ok) {
        setLearning(await learnRes.value.json());
      }
      if (botRes.status === "fulfilled" && botRes.value.ok) {
        const bd = await botRes.value.json();
        // Build a map of current bot configs keyed by symbol
        const cfgMap = {};
        for (const b of (bd.bots || [])) {
          if (b.symbol) cfgMap[b.symbol] = b;
        }
        setCurrentBotConfig(cfgMap);
      }
    } catch (e) {
      console.error("Autopilot fetch error:", e);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchAll();
    const iv = setInterval(fetchAll, 15000);
    return () => clearInterval(iv);
  }, [fetchAll]);

  const doAction = async (endpoint, method = "POST") => {
    setActionLoading(true);
    setMessage(null);
    try {
      const r = await fetch(`${API}/api/${endpoint}`, { method, headers });
      const data = await r.json();
      setMessage({ type: r.ok ? "success" : "error", text: data.message || data.status || JSON.stringify(data) });
      setTimeout(() => fetchAll(), 2000);
    } catch (e) {
      setMessage({ type: "error", text: e.message });
    }
    setActionLoading(false);
  };

  const openDeployModal = (gem) => {
    setDeployModal({ gem });
  };

  const confirmDeploy = async (gem, botId = "dca-master") => {
    setDeployLoading(true);
    try {
      const r = await fetch(`${API}/api/autopilot/deploy/${gem.symbol}?bot_id=${botId}`, {
        method: "POST", headers,
      });
      const data = await r.json();
      setMessage({ type: r.ok ? "success" : "error", text: r.ok ? `✅ ${gem.base} config deployed to ${botId}!` : (data.detail || "Deploy failed") });
      setDeployModal(null);
      setTimeout(() => fetchAll(), 2000);
    } catch (e) {
      setMessage({ type: "error", text: e.message });
    }
    setDeployLoading(false);
  };

  const phaseEmoji = {
    idle: "\u23F8\uFE0F",
    discovering: "\uD83D\uDD0D",
    quick_filtering: "\u26A1",
    evolving: "\uD83E\uDDEC",
    learning: "\uD83D\uDCDA",
    regime_watch: "\uD83D\uDCE1",
    sleeping: "\uD83D\uDCA4",
    stopped: "\u23F9\uFE0F",
    error_recovery: "\u26A0\uFE0F",
  };

  const regimeColor = (r) => {
    if (r === "uptrend") return "green";
    if (r === "downtrend") return "red";
    return "yellow";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin w-8 h-8 border-2 border-teal-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  const isRunning = status?.running;

  return (
    <div className="space-y-6">
      {/* Header + Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-mono font-bold text-zinc-100 flex items-center gap-2">
            <span className="text-2xl">{"\uD83E\uDD16"}</span> Skynet Autopilot
            {isRunning && (
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-teal-500" />
              </span>
            )}
          </h2>
          <p className="text-xs text-zinc-500 mt-1 font-mono">
            Continuous coin discovery & strategy evolution
          </p>
        </div>
        <div className="flex gap-2">
          {!isRunning ? (
            <button
              onClick={() => doAction("autopilot/start")}
              disabled={actionLoading}
              className="px-4 py-2 text-xs font-mono bg-teal-500/10 text-teal-400 border border-teal-500/30 rounded-lg hover:bg-teal-500/20 transition-all disabled:opacity-50"
            >
              {actionLoading ? "Starting..." : "\u25B6 Start Autopilot"}
            </button>
          ) : (
            <button
              onClick={() => doAction("autopilot/stop")}
              disabled={actionLoading}
              className="px-4 py-2 text-xs font-mono bg-red-500/10 text-red-400 border border-red-500/30 rounded-lg hover:bg-red-500/20 transition-all disabled:opacity-50"
            >
              {actionLoading ? "Stopping..." : "\u23F9 Stop"}
            </button>
          )}
          <button
            onClick={fetchAll}
            className="px-3 py-2 text-xs font-mono text-zinc-400 border border-zinc-700 rounded-lg hover:bg-zinc-800 transition-all"
          >
            {"\u21BB"} Refresh
          </button>
        </div>
      </div>

      {message && (
        <div className={`p-3 rounded-lg text-xs font-mono border ${
          message.type === "success" ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-400" : "bg-red-500/10 border-red-500/20 text-red-400"
        }`}>
          {message.text}
        </div>
      )}

      {/* Status Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-lg p-3">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Status</div>
          <div className="text-sm font-mono font-semibold text-zinc-100">
            {phaseEmoji[status?.phase] || ""} {status?.phase || "unknown"}
          </div>
        </div>
        <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-lg p-3">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Cycle</div>
          <div className="text-sm font-mono font-semibold text-teal-400">#{status?.cycle || 0}</div>
        </div>
        <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-lg p-3">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Coins Scanned</div>
          <div className="text-sm font-mono font-semibold text-blue-400">{status?.coins_scanned || 0}</div>
        </div>
        <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-lg p-3">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Gems Found</div>
          <div className="text-sm font-mono font-semibold text-amber-400">{"\uD83D\uDC8E"} {status?.gems_found || 0}</div>
        </div>
      </div>

      {/* Evolution Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-lg p-3">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Evolutions Run</div>
          <div className="text-sm font-mono font-semibold text-purple-400">{"\uD83E\uDDEC"} {status?.total_evolutions || 0}</div>
        </div>
        <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-lg p-3">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Deals Learned</div>
          <div className="text-sm font-mono font-semibold text-emerald-400">{"\uD83D\uDCDA"} {status?.total_deals_learned || 0}</div>
        </div>
        <div className="bg-zinc-900/60 border border-zinc-800/60 rounded-lg p-3">
          <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-1">Next Cycle</div>
          <div className="text-sm font-mono font-semibold text-zinc-300">{status?.cycle_interval_minutes || 120}min</div>
        </div>
      </div>

      {/* Gem Board */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4">
        <h3 className="text-sm font-mono font-bold text-amber-400 mb-3 flex items-center gap-2">
          {"\uD83D\uDC8E"} Gem Board — Top Evolved Coins
        </h3>
        {gems.length === 0 ? (
          <div className="text-center py-8 text-zinc-500 text-xs font-mono">
            {isRunning ? "Autopilot is scanning... gems will appear after first cycle completes" : "Start the autopilot to discover gems"}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-mono">
              <thead>
                <tr className="text-zinc-500 border-b border-zinc-800">
                  <th className="text-left py-2 px-2">#</th>
                  <th className="text-left py-2 px-2">Coin</th>
                  <th className="text-right py-2 px-2">APY</th>
                  <th className="text-right py-2 px-2">DD</th>
                  <th className="text-right py-2 px-2">WR</th>
                  <th className="text-right py-2 px-2">Deals</th>
                  <th className="text-right py-2 px-2">D/Mo</th>
                  <th className="text-center py-2 px-2">Regime</th>
                  <th className="text-right py-2 px-2">Score</th>
                  <th className="text-center py-2 px-2">Deploy</th>
                </tr>
              </thead>
              <tbody>
                {gems.map((g, i) => (
                  <tr key={g.symbol + i} className="border-b border-zinc-800/50 hover:bg-zinc-800/30 transition-colors">
                    <td className="py-2.5 px-2 text-zinc-500">{i + 1}</td>
                    <td className="py-2.5 px-2">
                      <span className="text-zinc-100 font-semibold">{g.base}</span>
                      <span className="text-zinc-600 ml-1 text-[10px]">/{g.symbol?.replace(g.base, "")}</span>
                    </td>
                    <td className={`py-2.5 px-2 text-right font-semibold ${g.apy > 50 ? "text-emerald-400" : g.apy > 20 ? "text-teal-400" : "text-zinc-300"}`}>
                      {g.apy?.toFixed(1)}%
                    </td>
                    <td className={`py-2.5 px-2 text-right ${g.max_dd > 50 ? "text-red-400" : g.max_dd > 30 ? "text-amber-400" : "text-emerald-400"}`}>
                      {g.max_dd?.toFixed(1)}%
                    </td>
                    <td className="py-2.5 px-2 text-right text-zinc-300">{g.win_rate?.toFixed(0)}%</td>
                    <td className="py-2.5 px-2 text-right text-zinc-400">{g.deals}</td>
                    <td className="py-2.5 px-2 text-right text-zinc-400">{g.deals_per_month?.toFixed(1)}</td>
                    <td className="py-2.5 px-2 text-center">
                      <Badge color={regimeColor(g.regime)}>{g.regime || "?"}</Badge>
                    </td>
                    <td className="py-2.5 px-2 text-right">
                      <span className={`font-semibold ${g.fitness > 0.7 ? "text-teal-400" : g.fitness > 0.5 ? "text-blue-400" : "text-zinc-400"}`}>
                        {g.fitness?.toFixed(3)}
                      </span>
                    </td>
                    <td className="py-2.5 px-2 text-center">
                      <button
                        onClick={() => openDeployModal(g)}
                        className="px-2 py-1 text-[10px] font-mono bg-teal-500/10 text-teal-400 border border-teal-500/20 rounded hover:bg-teal-500/20 transition-all"
                      >
                        🚀 Deploy
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Learning Insights */}
      {learning && learning.deal_count > 0 && (
        <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4">
          <h3 className="text-sm font-mono font-bold text-emerald-400 mb-3 flex items-center gap-2">
            {"\uD83E\uDDE0"} Learning Insights — {learning.deal_count} Real Trades Analyzed
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
            <div className="bg-zinc-800/40 rounded-lg p-2.5">
              <div className="text-[10px] text-zinc-500">Win Rate</div>
              <div className="text-sm font-mono font-semibold text-emerald-400">{learning.win_rate?.toFixed(1)}%</div>
            </div>
            <div className="bg-zinc-800/40 rounded-lg p-2.5">
              <div className="text-[10px] text-zinc-500">Avg Profit</div>
              <div className="text-sm font-mono font-semibold text-teal-400">${learning.avg_profit?.toFixed(2)}</div>
            </div>
            <div className="bg-zinc-800/40 rounded-lg p-2.5">
              <div className="text-[10px] text-zinc-500">Avg SOs</div>
              <div className="text-sm font-mono font-semibold text-blue-400">{learning.avg_so?.toFixed(1)}</div>
            </div>
            <div className="bg-zinc-800/40 rounded-lg p-2.5">
              <div className="text-[10px] text-zinc-500">Avg Duration</div>
              <div className="text-sm font-mono font-semibold text-purple-400">{learning.avg_duration_h?.toFixed(1)}h</div>
            </div>
          </div>

          {learning.analysis && (
            <div className="bg-zinc-800/30 rounded-lg p-3 text-xs font-mono text-zinc-400">
              <span className="text-amber-400">{"\uD83D\uDCA1"} Analysis:</span> {learning.analysis}
            </div>
          )}

          {/* Winners vs Losers */}
          {learning.winners_vs_losers && (
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-lg p-2.5">
                <div className="text-[10px] text-emerald-500/70">Winners ({learning.winners_vs_losers.winners})</div>
                <div className="text-xs font-mono text-zinc-300 mt-1">
                  Avg SO: {learning.winners_vs_losers.avg_winner_so?.toFixed(1)} |
                  Dur: {learning.winners_vs_losers.avg_winner_dur_h?.toFixed(1)}h
                </div>
              </div>
              <div className="bg-red-500/5 border border-red-500/10 rounded-lg p-2.5">
                <div className="text-[10px] text-red-500/70">Losers ({learning.winners_vs_losers.losers})</div>
                <div className="text-xs font-mono text-zinc-300 mt-1">
                  Avg SO: {learning.winners_vs_losers.avg_loser_so?.toFixed(1)} |
                  Dur: {learning.winners_vs_losers.avg_loser_dur_h?.toFixed(1)}h
                </div>
              </div>
            </div>
          )}

          {/* Per-Coin Breakdown */}
          {learning.per_coin && Object.keys(learning.per_coin).length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] text-zinc-500 uppercase tracking-wider mb-2">Per-Coin Performance</div>
              <div className="space-y-1">
                {Object.entries(learning.per_coin).map(([sym, stats]) => (
                  <div key={sym} className="flex items-center justify-between bg-zinc-800/30 rounded px-3 py-1.5 text-xs font-mono">
                    <span className="text-zinc-200">{sym}</span>
                    <div className="flex gap-3 text-zinc-400">
                      <span>{stats.deals}d</span>
                      <span className={stats.total_profit > 0 ? "text-emerald-400" : "text-red-400"}>
                        ${stats.total_profit?.toFixed(2)}
                      </span>
                      <span>WR {stats.win_rate?.toFixed(0)}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Regime Map */}
      {status?.regime_map && Object.keys(status.regime_map).length > 0 && (
        <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4">
          <h3 className="text-sm font-mono font-bold text-blue-400 mb-3">
            {"\uD83C\uDF0D"} Market Regimes
          </h3>
          <div className="flex flex-wrap gap-2">
            {Object.entries(status.regime_map).map(([sym, regime]) => (
              <div key={sym} className="flex items-center gap-1.5 bg-zinc-800/40 rounded-lg px-3 py-1.5">
                <span className="text-xs font-mono text-zinc-300">{sym}</span>
                <Badge color={regimeColor(regime)}>{regime}</Badge>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error Display */}
      {status?.error && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-xs font-mono text-red-400">
          {"\u26A0\uFE0F"} Error: {status.error}
        </div>
      )}

      {/* ═══ DEPLOY MODAL WITH CONFIG DIFF ═══ */}
      {deployModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setDeployModal(null)}>
          <div className="bg-zinc-900 border border-zinc-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="p-5 border-b border-zinc-800 flex items-center justify-between">
              <div>
                <h3 className="text-base font-mono font-bold text-zinc-100 flex items-center gap-2">
                  🚀 Deploy <span className="text-teal-400">{deployModal.gem.base}</span> Config
                </h3>
                <p className="text-xs text-zinc-500 mt-0.5">Review evolved config vs current — then confirm deploy</p>
              </div>
              <button onClick={() => setDeployModal(null)} className="text-zinc-500 hover:text-zinc-300 text-xl">✕</button>
            </div>

            {/* Metrics summary */}
            <div className="p-4 grid grid-cols-3 gap-3 border-b border-zinc-800">
              <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-lg p-3 text-center">
                <div className="text-[10px] text-emerald-500/70 uppercase">APY</div>
                <div className="text-lg font-mono font-bold text-emerald-400">{deployModal.gem.apy?.toFixed(1)}%</div>
              </div>
              <div className={`border rounded-lg p-3 text-center ${deployModal.gem.max_dd < 25 ? "bg-emerald-500/5 border-emerald-500/10" : "bg-amber-500/5 border-amber-500/10"}`}>
                <div className="text-[10px] text-zinc-500 uppercase">Max DD</div>
                <div className={`text-lg font-mono font-bold ${deployModal.gem.max_dd < 25 ? "text-emerald-400" : "text-amber-400"}`}>{deployModal.gem.max_dd?.toFixed(1)}%</div>
              </div>
              <div className="bg-blue-500/5 border border-blue-500/10 rounded-lg p-3 text-center">
                <div className="text-[10px] text-blue-500/70 uppercase">Win Rate</div>
                <div className="text-lg font-mono font-bold text-blue-400">{deployModal.gem.win_rate?.toFixed(0)}%</div>
              </div>
            </div>

            {/* Config diff table */}
            <div className="p-4">
              <h4 className="text-xs font-mono text-zinc-400 uppercase tracking-wider mb-3">
                🧬 Evolved Config vs Current Bot
              </h4>
              <div className="space-y-1.5">
                {CONFIG_FIELDS.map(field => {
                  const evolvedVal = deployModal.gem.dna?.[field.key];
                  const currentBot = currentBotConfig?.[deployModal.gem.symbol];
                  const currentVal = currentBot?.[field.key.replace("bo_pct","base_order_pct").replace("so_pct","safety_order_pct").replace("trailing","trailing_tp").replace("tp","take_profit_pct").replace("step","safety_order_step_pct").replace("vol_scale","safety_order_volume_scale").replace("step_scale","safety_order_step_scale").replace("max_so","max_safety_orders").replace("compound_cap","compound_cap")];

                  if (evolvedVal === undefined) return null;

                  const hasChange = currentVal !== undefined && Math.abs(Number(evolvedVal) - Number(currentVal)) > 0.01;

                  return (
                    <div key={field.key} className={`flex items-center justify-between rounded-lg px-3 py-2 text-xs font-mono ${hasChange ? "bg-amber-500/5 border border-amber-500/10" : "bg-zinc-800/30"}`}>
                      <span className="text-zinc-400">{field.label}</span>
                      <div className="flex items-center gap-3">
                        {currentVal !== undefined && (
                          <span className="text-zinc-600">{field.fmt(currentVal)}</span>
                        )}
                        {currentVal !== undefined && hasChange && (
                          <span className="text-zinc-600">→</span>
                        )}
                        <span className={hasChange ? "text-amber-400 font-semibold" : "text-zinc-200"}>
                          {field.fmt(evolvedVal)}
                        </span>
                        {hasChange && <span className="text-amber-400 text-[9px]">CHANGED</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Bot selector + confirm */}
            <div className="p-4 border-t border-zinc-800">
              <p className="text-xs text-zinc-500 font-mono mb-3">
                ⚠️ This queues an <code className="text-amber-400">update_config</code> command — bot will apply it on next heartbeat.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => confirmDeploy(deployModal.gem, "dca-master")}
                  disabled={deployLoading}
                  className="flex-1 py-2.5 text-xs font-mono bg-teal-500/10 text-teal-400 border border-teal-500/30 rounded-lg hover:bg-teal-500/20 transition-all disabled:opacity-50"
                >
                  {deployLoading ? "Deploying..." : "✅ Deploy to dca-master (RENDER)"}
                </button>
                <button
                  onClick={() => confirmDeploy(deployModal.gem, "dca-near")}
                  disabled={deployLoading}
                  className="flex-1 py-2.5 text-xs font-mono bg-blue-500/10 text-blue-400 border border-blue-500/30 rounded-lg hover:bg-blue-500/20 transition-all disabled:opacity-50"
                >
                  {deployLoading ? "Deploying..." : "Deploy to dca-near (NEAR)"}
                </button>
              </div>
              <button
                onClick={() => setDeployModal(null)}
                className="w-full mt-2 py-2 text-xs font-mono text-zinc-500 hover:text-zinc-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
